/**
 * Composable for managing category page data and metadata.
 *
 * @returns {Object} An object containing computed properties related to category page information.
 * @property {ComputedRef<string>} title - The title of the category page.
 * @property {ComputedRef<string>} subtitle - The subtitle of the category page.
 * @property {ComputedRef<string>} bannerImage - The URL or path of the category banner image.
 * @property {ComputedRef<string>} seoText - SEO-related text for the category page.
 * @property {ComputedRef<number>} lvlCategory - The category level (defaults to 1 if not specified).
 * @property {ComputedRef<boolean>} isSubcategory - Indicates whether the current page is a subcategory.
 * @property {ComputedRef<string>} parentCategory - The parent category of the current category.
 * @property {ComputedRef<Array>} editorialContent - Filtered editorial content with logic wrapper.
 *
 * @example
 * const { title, subtitle, bannerImage } = useCategoryPage()
 * console.log(title.value) // Outputs the category title
 */
export const useCategoryPage = () => {
  const data = useTemplatePlpState()
  const title = computed(() => data?.value?.categoryTitle ?? '')

  const subtitle = computed(() => data?.value?.categorySubTitle ?? '')

  const bannerImage = computed(() => data?.value?.bannerImage ?? '')

  const seoText = computed(() => data?.value?.seoText ?? '')

  const parentCategory = computed(
    // @ts-ignore
    () => data?.value?.parentPage ?? ''
  )

  const lvlCategory = computed(() =>
    data?.value?.lvlCategory ? Number(data.value.lvlCategory) : 1
  )

  const isSubcategory = computed(() => data?.value?.subcategory ?? false)
  const editorialContent = computed(() => {
    const content = data?.value?.logicWrapper?.bandLogic ?? []
    return content.map((item: { listOfLogicWrapper: any[] }) => {
      const filteredLogicWrapper = item.listOfLogicWrapper.filter(wrapper => {
        const showInAllCountries = wrapper.showInAllCountries
        const showInCountry = wrapper.showInCountry
        // If both are undefined, return true
        if (showInAllCountries === undefined && showInCountry === undefined) {
          return true
        }
        // If showInAllCountries is false, check showInCountry
        if (showInAllCountries === false) {
          return showInCountry !== false
        }
        // Otherwise, return true
        return true
      })
      return {
        ...item,
        listOfLogicWrapper: filteredLogicWrapper,
      }
    })
  })
  return {
    title,
    subtitle,
    bannerImage,
    seoText,
    lvlCategory,
    isSubcategory,
    parentCategory,
    editorialContent,
  }
}
